import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import stationManagementService, { StationWithDetails, Operator } from '../../services/stationManagementService';
import operatorService from '../../services/operatorService';
import productionService from '../../services/productionService';
import { wsService } from '../../services/websocket';
import { useLanguage } from '../../i18n/LanguageContext';
import { usePageTitle } from '../../contexts/PageTitleContext';
import featureFlags from '../../config/featureFlags';
import StationCard from './components/StationCard';
import StationManagementModals from './components/StationManagementModals';

const SupervisorStationManagement: React.FC = () => {
  const { t } = useLanguage();
  usePageTitle(t('supervisor.stationManagement'), t('nav.settings'));
  const queryClient = useQueryClient();
  const [showAssignSessionModal, setShowAssignSessionModal] = useState(false);
  const [selectedStation, setSelectedStation] = useState<StationWithDetails | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState('');

  // Operator assignment states
  const [showAssignOperatorModal, setShowAssignOperatorModal] = useState(false);
  const [showOperatorPromptModal, setShowOperatorPromptModal] = useState(false);
  const [selectedOperatorId, setSelectedOperatorId] = useState('');
  const [stationForOperatorAssignment, setStationForOperatorAssignment] = useState<StationWithDetails | null>(null);

  // Material restriction states
  const [stationMaterialTypes, setStationMaterialTypes] = useState<Record<string, string>>({});
  const [showMaterialChangeConfirm, setShowMaterialChangeConfirm] = useState(false);
  const [pendingMaterialChange, setPendingMaterialChange] = useState<{ stationId: number; newType: string } | null>(null);

  // Hide disabled stations toggle - persisted to localStorage
  const [hideDisabledStations, setHideDisabledStations] = useState(() => {
    return localStorage.getItem('hideDisabledStations') === 'true';
  });

  // Persist hide disabled stations preference
  const toggleHideDisabledStations = () => {
    const newValue = !hideDisabledStations;
    setHideDisabledStations(newValue);
    localStorage.setItem('hideDisabledStations', String(newValue));
  };

  // Fetch stations with details
  const { data: stations, isLoading: stationsLoading } = useQuery(
    ['supervisor-stations'],
    stationManagementService.getStations,
    {
      refetchInterval: 30000, // Backup polling every 30 seconds
      staleTime: 5000, // Consider data stale after 5 seconds
      refetchOnMount: 'always', // Always refetch when component mounts (navigation)
      refetchOnWindowFocus: true, // Refetch when tab gains focus
    }
  );

  // Initialize material types from database when stations are loaded
  useEffect(() => {
    if (stations) {
      const materialTypesFromDb: Record<string, string> = {};
      stations.forEach(station => {
        if (station.allowed_material_type) {
          materialTypesFromDb[station.station_id] = station.allowed_material_type;
        }
      });
      setStationMaterialTypes(materialTypesFromDb);
    }
  }, [stations]);

  // Fetch planned sessions for assignment (only ones without stations)
  const { data: plannedSessionsData } = useQuery(
    ['planned-sessions'],
    () => productionService.getProductionSessions({ unassigned: true }),
    {
      enabled: showAssignSessionModal,
    }
  );

  const plannedSessions = plannedSessionsData || [];

  // Fetch operators for assignment
  const { data: operators } = useQuery<Operator[]>(
    ['operators'],
    () => stationManagementService.getOperators(),
    {
      enabled: showAssignOperatorModal || showOperatorPromptModal,
    }
  );

  // Fetch registered printers from config_settings (printer_* entries)
  const { data: registeredPrinters = [] } = useQuery(
    ['registered-printers'],
    async () => {
      const all = await productionService.getConfigSettings();
      return all.filter(s => s.setting_key.startsWith('printer_') && s.setting_key !== 'printer_subnet');
    },
    { refetchOnMount: 'always' }
  );

  // Assign printer to station mutation
  const assignPrinterMutation = useMutation(
    async ({ stationId, printerMacId }: { stationId: number; printerMacId: string | null }) => {
      return stationManagementService.updateStationPrinter(stationId, printerMacId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('supervisor-stations');
        toast.success(t('stations.printerAssigned'));
      },
      onError: () => { toast.error(t('stations.testPrintFailed')); },
    }
  );

  // WebSocket listeners
  useEffect(() => {
    // Connect to WebSocket
    wsService.connect();

    const handleStationUpdate = () => {
      queryClient.invalidateQueries(['supervisor-stations']);
    };

    const handleSessionUpdate = () => {
      queryClient.invalidateQueries(['supervisor-stations']);
      queryClient.invalidateQueries(['planned-sessions']);
      queryClient.invalidateQueries(['operators']); // Refresh available operators when session changes
    };

    const handleBoxLifecycleUpdate = () => {
      queryClient.invalidateQueries(['supervisor-stations']);
      queryClient.invalidateQueries(['planned-sessions']);
    };

    const handleSessionCreated = () => {
      queryClient.invalidateQueries(['supervisor-stations']);
      queryClient.invalidateQueries(['planned-sessions']);
    };

    wsService.on('station-updated', handleStationUpdate);
    wsService.on('production-session-updated', handleSessionUpdate);
    wsService.on('production-session-created', handleSessionCreated);
    wsService.on('box-packed', handleBoxLifecycleUpdate);
    wsService.on('box-cancelled', handleBoxLifecycleUpdate);
    wsService.on('box-deleted', handleBoxLifecycleUpdate);
    wsService.on('pending-label-voided', handleBoxLifecycleUpdate);

    return () => {
      wsService.off('station-updated', handleStationUpdate);
      wsService.off('production-session-updated', handleSessionUpdate);
      wsService.off('production-session-created', handleSessionCreated);
      wsService.off('box-packed', handleBoxLifecycleUpdate);
      wsService.off('box-cancelled', handleBoxLifecycleUpdate);
      wsService.off('box-deleted', handleBoxLifecycleUpdate);
      wsService.off('pending-label-voided', handleBoxLifecycleUpdate);
    };
  }, [queryClient]);

  // Assign session mutation
  const assignSessionMutation = useMutation(
    (data: { stationId: number; sessionId: string }) =>
      stationManagementService.assignSession(data.stationId, data.sessionId),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(['supervisor-stations']);
        queryClient.invalidateQueries(['planned-sessions']);
        queryClient.invalidateQueries(['productionSessions']); // Update Production Planning page
        queryClient.invalidateQueries(['stations']); // Update station list in other pages
        setShowAssignSessionModal(false);
        setSelectedSessionId('');
        toast.success(t('supervisor.sessionAssignedSuccess'));

        // Show operator assignment prompt modal
        if (selectedStation) {
          setStationForOperatorAssignment(selectedStation);
          setShowOperatorPromptModal(true);
        }
        setSelectedStation(null);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || error?.message || t('supervisor.failedToAssignSession');
        toast.error(errorMessage);
      },
    }
  );

  // Start session mutation — routes through operator-service with sub_session_number
  const startSessionMutation = useMutation(
    (subSessionNumber: string) => operatorService.startSession(subSessionNumber, '', true),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['supervisor-stations']);
        queryClient.invalidateQueries(['productionSessions']);
        queryClient.invalidateQueries(['stations']);
        toast.success(t('supervisor.sessionStartedSuccess'));
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || error?.message || t('supervisor.failedToStartSession');
        toast.error(errorMessage);
      },
    }
  );

  // Pause session mutation — routes through operator-service with sub_session_number
  const pauseSessionMutation = useMutation(
    (subSessionNumber: string) => operatorService.pauseSession(subSessionNumber, 'supervisor_pause', true),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['supervisor-stations']);
        queryClient.invalidateQueries(['productionSessions']);
        queryClient.invalidateQueries(['stations']);
        toast.success(t('supervisor.sessionPausedSuccess'));
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || error?.message || t('supervisor.failedToPauseSession');
        toast.error(errorMessage);
      },
    }
  );

  // Resume session mutation — routes through operator-service with sub_session_number
  const resumeSessionMutation = useMutation(
    (subSessionNumber: string) => operatorService.resumeSession(subSessionNumber, undefined, true),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['supervisor-stations']);
        queryClient.invalidateQueries(['productionSessions']);
        queryClient.invalidateQueries(['stations']);
        toast.success(t('supervisor.sessionResumedSuccess'));
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || error?.message || t('supervisor.failedToResumeSession');
        toast.error(errorMessage);
      },
    }
  );

  // Complete session mutation — routes through operator-service with sub_session_number
  const completeSessionMutation = useMutation(
    (data: { subSessionNumber: string; notes?: string }) =>
      operatorService.completeSession(data.subSessionNumber, data.notes, true),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['supervisor-stations']);
        queryClient.invalidateQueries(['productionSessions']);
        queryClient.invalidateQueries(['stations']);
        queryClient.invalidateQueries(['rawMaterials']);
        toast.success(t('supervisor.sessionCompletedSuccess'));
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || error?.message || t('supervisor.failedToCompleteSession');
        toast.error(errorMessage);
      },
    }
  );

  // Toggle station enabled mutation
  const toggleStationMutation = useMutation(
    (data: { stationId: number; enabled: boolean }) =>
      stationManagementService.toggleStationEnabled(data.stationId, data.enabled),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['supervisor-stations']);
        queryClient.invalidateQueries(['stations']);
        toast.success(variables.enabled ? t('supervisor.stationEnabled') : t('supervisor.stationDisabled'));
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || error?.message || t('supervisor.failedToToggleStation');
        toast.error(errorMessage);
      },
    }
  );

  // Unplan session mutation
  const unplanSessionMutation = useMutation(
    (stationId: number) => stationManagementService.unplanSession(stationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['supervisor-stations']);
        queryClient.invalidateQueries(['planned-sessions']);
        queryClient.invalidateQueries(['productionSessions']);
        queryClient.invalidateQueries(['stations']);
        toast.success(t('supervisor.sessionUnplannedSuccess'));
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || error?.message || t('supervisor.failedToUnplanSession');
        toast.error(errorMessage);
      },
    }
  );

  // Assign operator mutation
  const assignOperatorMutation = useMutation(
    (data: { stationId: number; operatorId: string }) =>
      stationManagementService.assignOperator(data.stationId, data.operatorId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['supervisor-stations']);
        queryClient.invalidateQueries(['operators']);
        setShowAssignOperatorModal(false);
        setShowOperatorPromptModal(false);
        setStationForOperatorAssignment(null);
        setSelectedOperatorId('');
        toast.success(t('supervisor.operatorAssignedSuccess'));
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || error?.message || t('supervisor.failedToAssignOperator');
        toast.error(errorMessage);
      },
    }
  );

  const handleMaterialTypeChange = async (stationId: number, newType: string) => {
    const currentType = stationMaterialTypes[stationId];
    
    if (currentType && currentType !== newType) {
      // Show cleaning confirmation dialog
      setPendingMaterialChange({ stationId, newType });
      setShowMaterialChangeConfirm(true);
    } else {
      // First time setting or same type - save directly to database
      try {
        await stationManagementService.updateMaterialType(stationId, newType);
        setStationMaterialTypes(prev => ({ ...prev, [stationId]: newType }));
        toast.success(t('supervisor.materialTypeUpdated'));
      } catch (error: any) {
        const errorMessage = error?.response?.data?.error || error?.message || 'Failed to update material type';
        toast.error(errorMessage);
      }
    }
  };

  const confirmMaterialChange = async () => {
    if (pendingMaterialChange) {
      try {
        // Save to database
        await stationManagementService.updateMaterialType(
          pendingMaterialChange.stationId, 
          pendingMaterialChange.newType
        );
        // Update local state
        setStationMaterialTypes(prev => ({
          ...prev,
          [pendingMaterialChange.stationId]: pendingMaterialChange.newType
        }));
        setShowMaterialChangeConfirm(false);
        setPendingMaterialChange(null);
        toast.success(t('supervisor.materialTypeUpdated'));
      } catch (error: any) {
        const errorMessage = error?.response?.data?.error || error?.message || 'Failed to update material type';
        toast.error(errorMessage);
      }
    }
  };

  const cancelMaterialChange = () => {
    setShowMaterialChangeConfirm(false);
    setPendingMaterialChange(null);
  };

  const getMaterialCategory = (
    rawMaterialLot?: string, 
    rawMaterialItemNumber?: string,
    productName?: string,
    productSku?: string
  ): string => {
    // Combine all available fields for comprehensive checking
    const rawMaterial = (rawMaterialLot || '').toLowerCase();
    const itemNumber = (rawMaterialItemNumber || '').toLowerCase();
    const product = (productName || '').toLowerCase();
    const sku = (productSku || '').toLowerCase();
    const combined = `${rawMaterial} ${itemNumber} ${product} ${sku}`.toLowerCase();

    // Check for Chicken keywords
    if (combined.includes('chicken') || combined.includes('雞') || 
        combined.includes('chk') || combined.includes('chick')) {
      return 'Chicken';
    }
    
    // Check for Beef keywords
    if (combined.includes('beef') || combined.includes('牛') || 
        combined.includes('omasum') || combined.includes('bef') ||
        combined.includes('cattle') || combined.includes('cow')) {
      return 'Beef';
    }
    
    // Check for Pork keywords
    if (combined.includes('pork') || combined.includes('豬') || 
        combined.includes('prk') || combined.includes('pig') ||
        combined.includes('swine')) {
      return 'Pork';
    }
    
    // Check for Goat/Lamb keywords
    if (combined.includes('goat') || combined.includes('lamb') || 
        combined.includes('羊') || combined.includes('goa') || 
        combined.includes('lmb') || combined.includes('sheep') ||
        combined.includes('mutton')) {
      return 'Goat/Lamb';
    }
    
    return 'Other';
  };

  const handleAssignSession = (station: StationWithDetails) => {
    setSelectedStation(station);
    setShowAssignSessionModal(true);
  };

  const handleConfirmAssignSession = () => {
    if (selectedStation && selectedSessionId) {
      // Validate material type restriction
      const stationMaterialType = stationMaterialTypes[selectedStation.station_id];
      const selectedSession = plannedSessions.find((s: any) => s.session_number === selectedSessionId);
      
      // Material type restriction validation (only if feature is enabled)
      if (featureFlags.MATERIAL_TYPE_RESTRICTION) {

        if (stationMaterialType && selectedSession) {
          const sessionMaterialType = getMaterialCategory(
            selectedSession.raw_material_lot,
            selectedSession.raw_material_item_number,
            selectedSession.product_name,
            selectedSession.product_sku
          );

          if (sessionMaterialType !== 'Other' && sessionMaterialType !== stationMaterialType) {
            const materialInfo = selectedSession.product_name || selectedSession.raw_material_lot || 'Unknown';
            toast.error(
              `${t('supervisor.materialTypeMismatch')}: Station ${selectedStation.station_name} ${t('supervisor.restrictedTo')} ${stationMaterialType}. ${t('supervisor.sessionUses')} ${sessionMaterialType}. (${materialInfo})`
            );
            return;
          }

          // If material type is 'Other' (unknown), we need to prevent assignment to restricted stations
          if (sessionMaterialType === 'Other' && stationMaterialType) {
            const materialInfo = selectedSession.product_name || selectedSession.raw_material_lot || 'Unknown';
            toast.error(
              `${t('supervisor.cannotDetermineMaterialType')}: Station ${selectedStation.station_name} ${t('supervisor.restrictedTo')} ${stationMaterialType}. Cannot verify if session material matches. (${materialInfo})`
            );
            return;
          }
        }
      }
      
      assignSessionMutation.mutate({
        stationId: selectedStation.station_id,
        sessionId: selectedSessionId,
      });
    }
  };

  const handleStartSession = (station: StationWithDetails) => {
    if (!station.sub_session_number) { toast.error('No sub-session assigned to this station'); return; }
    startSessionMutation.mutate(station.sub_session_number);
  };

  const handlePauseSession = (station: StationWithDetails) => {
    if (!station.sub_session_number) { toast.error('No sub-session assigned to this station'); return; }
    pauseSessionMutation.mutate(station.sub_session_number);
  };

  const handleUnplanSession = (station: StationWithDetails) => {
    unplanSessionMutation.mutate(station.station_id);
  };

  const handleResumeSession = (station: StationWithDetails) => {
    if (!station.sub_session_number) { toast.error('No sub-session assigned to this station'); return; }
    resumeSessionMutation.mutate(station.sub_session_number);
  };

  const handleToggleStation = (station: StationWithDetails) => {
    // Prevent disabling station with active session
    if (station.enabled && station.session_number && station.session_status !== 'completed') {
      toast.error(t('supervisor.cannotDisableWithActiveSession'));
      return;
    }
    toggleStationMutation.mutate({
      stationId: station.station_id,
      enabled: !station.enabled,
    });
  };

  const handleCompleteSession = (station: StationWithDetails) => {
    if (!station.sub_session_number) { toast.error('No sub-session assigned to this station'); return; }

    // Use actual_output_weight from the session data (already tracked by operators when they pack boxes)
    const actualOutput = Number(station.actual_output_weight) || 0;
    const targetOutput = Number(station.target_output_weight) || 0;

    // Calculate completion percentage
    const completionPercent = targetOutput > 0 ? (actualOutput / targetOutput * 100).toFixed(2) : '0.00';

    // Show confirmation with current production status
    const confirmMessage = actualOutput > 0
      ? `Complete session ${station.session_number}?\n\n✓ Output: ${actualOutput.toFixed(2)} lbs\n✓ Target: ${targetOutput.toFixed(2)} lbs\n✓ Progress: ${completionPercent}%\n\nProceed to complete this session?`
      : `Complete session ${station.session_number}?\n\n⚠ WARNING: No boxes packed yet!\n⚠ Output: 0 lbs\n⚠ Target: ${targetOutput.toFixed(2)} lbs\n\nOperators should pack boxes before completing.\nAre you sure you want to complete this session with zero production?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    completeSessionMutation.mutate({
      subSessionNumber: station.sub_session_number,
      notes: actualOutput > 0
        ? `Completed by supervisor - Output: ${actualOutput.toFixed(2)} lbs (${completionPercent}% of target)`
        : `Completed by supervisor - WARNING: No production recorded`,
    });
  };

  // Group stations by location (optionally filtering out disabled)
  const groupedStations = React.useMemo(() => {
    if (!stations) return {};
    const filteredStations = hideDisabledStations
      ? stations.filter((s: StationWithDetails) => s.enabled !== false)
      : stations;
    return filteredStations.reduce((acc: Record<string, StationWithDetails[]>, station: StationWithDetails) => {
      const key = station.location_code || 'unassigned';
      if (!acc[key]) acc[key] = [];
      acc[key].push(station);
      return acc;
    }, {});
  }, [stations, hideDisabledStations]);

  // Count disabled stations for the toggle button
  const disabledStationsCount = React.useMemo(() => {
    if (!stations) return 0;
    return stations.filter((s: StationWithDetails) => s.enabled === false).length;
  }, [stations]);

  if (stationsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">{t('supervisor.loadingStations')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stations Grid */}
      {Object.entries(groupedStations).map(([locationCode, locationStations]) => {
        // Separate active/busy stations from idle stations
        const activeStations = locationStations.filter((s: StationWithDetails) =>
          s.session_number || s.station_status === 'active' || s.station_status === 'received' || s.station_status === 'paused'
        );
        const idleStations = locationStations.filter((s: StationWithDetails) =>
          !s.session_number && s.station_status !== 'active' && s.station_status !== 'received' && s.station_status !== 'paused'
        );

        return (
          <div key={locationCode} className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            {/* Location Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-white">{t('supervisor.location')}: {locationStations[0]?.location_name || locationCode}</h2>
                  <p className="text-sm text-indigo-200">
                    {locationStations.filter((s: StationWithDetails) => s.enabled !== false).length}/{locationStations.length} {t('supervisor.stationsEnabled')} •
                    {' '}{locationStations.filter((s: StationWithDetails) => s.station_status === 'active' && s.enabled !== false).length} {t('supervisor.active')}
                  </p>
                </div>
                {disabledStationsCount > 0 && (
                  <button
                    onClick={toggleHideDisabledStations}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      hideDisabledStations
                        ? 'bg-white/20 text-white hover:bg-white/30'
                        : 'bg-white/10 text-indigo-200 hover:bg-white/20'
                    }`}
                  >
                    {hideDisabledStations
                      ? `${t('supervisor.showDisabledStations')} (${disabledStationsCount})`
                      : `${t('supervisor.hideDisabledStations')} (${disabledStationsCount})`}
                  </button>
                )}
              </div>
            </div>

            {/* Station Grid */}
            <div className="p-6">
              {/* Active/Busy Stations - Full Cards */}
              {activeStations.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {activeStations.map((station) => (
                    <StationCard
                      key={station.station_id}
                      station={station}
                      stations={stations || []}
                      stationMaterialTypes={stationMaterialTypes}
                      registeredPrinters={registeredPrinters}
                      onToggleStation={handleToggleStation}
                      onAssignSession={handleAssignSession}
                      onAssignOperator={(s) => {
                        setStationForOperatorAssignment(s);
                        setShowAssignOperatorModal(true);
                      }}
                      onStartSession={handleStartSession}
                      onPauseSession={handlePauseSession}
                      onResumeSession={handleResumeSession}
                      onCompleteSession={handleCompleteSession}
                      onUnplanSession={handleUnplanSession}
                      onMaterialTypeChange={handleMaterialTypeChange}
                      onAssignPrinter={(stationId, printerMacId) => assignPrinterMutation.mutate({ stationId, printerMacId })}
                      toggleStationLoading={toggleStationMutation.isLoading}
                      startSessionLoading={startSessionMutation.isLoading}
                      pauseSessionLoading={pauseSessionMutation.isLoading}
                      resumeSessionLoading={resumeSessionMutation.isLoading}
                      completeSessionLoading={completeSessionMutation.isLoading}
                      unplanSessionLoading={unplanSessionMutation.isLoading}
                    />
                  ))}
                </div>
              )}

              {/* Idle Stations - Compact View */}
              {idleStations.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    {t('supervisor.idleStations')} ({idleStations.length})
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                    {idleStations.map((station: StationWithDetails) => (
                      <div
                        key={station.station_id}
                        className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border transition-all ${
                          station.enabled === false
                            ? 'opacity-40 bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700'
                            : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${station.enabled === false ? 'bg-gray-300' : 'bg-gray-400'}`} />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{station.station_name}</div>
                            <div className="text-xs text-gray-400 dark:text-gray-500 truncate">
                              {station.enabled === false ? t('supervisor.disabled') : t('supervisor.idle')}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => handleToggleStation(station)}
                            disabled={toggleStationMutation.isLoading}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                              station.enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                            }`}
                            title={station.enabled ? t('supervisor.clickToDisable') : t('supervisor.clickToEnable')}
                          >
                            <span
                              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                station.enabled ? 'translate-x-5' : 'translate-x-1'
                              }`}
                            />
                          </button>
                          <button
                            onClick={() => handleAssignSession(station)}
                            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            disabled={!station.enabled}
                          >
                            {t('supervisor.assign')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      <StationManagementModals
        showAssignSessionModal={showAssignSessionModal}
        selectedStation={selectedStation}
        selectedSessionId={selectedSessionId}
        onSelectedSessionIdChange={setSelectedSessionId}
        plannedSessions={plannedSessions}
        onConfirmAssignSession={handleConfirmAssignSession}
        onCloseAssignSession={() => {
          setShowAssignSessionModal(false);
          setSelectedStation(null);
          setSelectedSessionId('');
        }}
        assignSessionLoading={assignSessionMutation.isLoading}
        showMaterialChangeConfirm={showMaterialChangeConfirm}
        pendingMaterialChange={pendingMaterialChange}
        stationMaterialTypes={stationMaterialTypes}
        onConfirmMaterialChange={confirmMaterialChange}
        onCancelMaterialChange={cancelMaterialChange}
        showOperatorPromptModal={showOperatorPromptModal}
        stationForOperatorAssignment={stationForOperatorAssignment}
        selectedOperatorId={selectedOperatorId}
        onSelectedOperatorIdChange={setSelectedOperatorId}
        operators={operators}
        onAssignOperator={(stationId, operatorId) => {
          assignOperatorMutation.mutate({ stationId, operatorId });
        }}
        onCloseOperatorPrompt={() => {
          setShowOperatorPromptModal(false);
          setStationForOperatorAssignment(null);
          setSelectedOperatorId('');
        }}
        assignOperatorLoading={assignOperatorMutation.isLoading}
        showAssignOperatorModal={showAssignOperatorModal}
        onCloseAssignOperator={() => {
          setShowAssignOperatorModal(false);
          setStationForOperatorAssignment(null);
          setSelectedOperatorId('');
        }}
      />

    </div>
  );
};

export default SupervisorStationManagement;
